import {
  lazy, Suspense, useEffect, useId, useRef, useState,
} from 'react';
import { useTokens } from '../../shared/theme.jsx';
import {
  Button, ErrorState, Field, Note, STAT_GRID_COLS, Stat, StatRow, StatusBadge, num, pct,
} from '../../shared/uiKit.jsx';
import { fmtHours, fmtSavedShare } from '../../shared/timeSavedFormat.js';
import { readTimeSavedHint, writeTimeSavedHint } from '../../shared/timeSavedHint.js';
import {
  getInstitutionAnalytics,
  getInstitutionContext,
  getSeats,
  listFundingRequests,
  listMyPermissionRequests,
  listPermissionRequests,
  listSeatRequests,
  requestInstitutionPermission,
  renameInstitutionPortalSlug,
  setInstitutionLogoSource,
  updateInstitutionBranding,
  uploadInstitutionLogo,
} from '../../shared/services/institutions.js';
import {
  buildPortalAuthUrl,
  navigateToPortalAuth,
} from '../../shared/services/auth.js';
import InstitutionMark from '../../shared/InstitutionMark.jsx';
import InstitutionLogoPicker from '../../shared/InstitutionLogoPicker.jsx';
import AnalyticsPanel from '../../shared/panels/AnalyticsPanel.jsx';
import SeatsPanel from '../../shared/panels/SeatsPanel.jsx';
import OveragePanel from '../../shared/panels/OveragePanel.jsx';
import EnterpriseUsagePanel from '../../shared/panels/EnterpriseUsagePanel.jsx';
import StudentRosterPanel from '../../shared/panels/StudentRosterPanel.jsx';
import AdminsPanel from '../../shared/panels/AdminsPanel.jsx';
import InvoicesPanel from '../../shared/panels/InvoicesPanel.jsx';
import PermissionRequestsPanel from '../../shared/panels/PermissionRequestsPanel.jsx';
import { notifyError, notifySuccess } from '../../shared/toast.js';
import PortalAuditView from './PortalAuditView.jsx';
import PortalSupportView from './PortalSupportView.jsx';
import { VIEW_TO_PATH } from '../portalUrl.js';
import InstitutionAiView, { InstitutionAiSkeleton } from './InstitutionAiView.jsx';

const TrendChart = lazy(() => { return import('../../shared/TrendChart.jsx'); });

// A rejected Overview read resolves to FAILED, so a failure is never mistaken
// for a privacy-suppressed report and one failed read never blanks the others.
// Promise.resolve().then() also turns a synchronous throw into a rejection.
const FAILED = Symbol('failed');
const settle = (load) => {
  return Promise.resolve().then(() => { return load(); }).catch(() => { return FAILED; });
};

// Every key the Network growth charts plot; all of them zero means no activity.
const TREND_KEYS = [
  'students', 'studentsNetworking', 'coffeeChats', 'alumniReached',
  'emailsSent', 'followUps', 'emailOpens', 'coffeeChatMinutes',
];

// The fifth Overview stat (time saved) spans the row where StatRow lays five
// cells 2 across, so it is never orphaned; the skeleton draws it the same way.
const FIFTH_STAT_SPAN = 'min-[360px]:col-span-2 sm:col-span-1';

export default function PortalHome({
  slug,
  branding,
  accent,
  profile,
  activeView,
  aiConversationId = null,
  onAiConversationChange,
  onNavigate,
  onBrandingChange,
  forceSignedOut = false,
  onAuthChange,
  onAuthRedirect,
  onProgramStatusChange,
}) {
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(true);
  const [context, setContext] = useState(null);
  const [contextError, setContextError] = useState(false);
  const [contextRetry, setContextRetry] = useState(0);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendWeeks, setTrendWeeks] = useState(12);
  // The window the shown analytics belong to; moves only on a successful read.
  const [loadedTrendWeeks, setLoadedTrendWeeks] = useState(12);
  const [seats, setSeats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [seatsError, setSeatsError] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(false);
  const [overviewRetry, setOverviewRetry] = useState(0);
  // Priority-action signals; null until their reads settle.
  const [signals, setSignals] = useState(null);
  const authRedirectStarted = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setContextError(false);
    (async () => {
      try {
        const ctx = await getInstitutionContext();
        if (active) {
          setContext(ctx);
          setAuthed(true);
          onAuthChange?.(true, ctx);
        }
      } catch (err) {
        if (!active) { return; }
        if (err?.response?.status === 401) {
          setAuthed(false);
          onAuthChange?.(false);
        } else {
          setContext(null);
          setContextError(true);
          onAuthChange?.(false);
        }
      } finally {
        if (active) { setLoading(false); }
      }
    })();
    return () => { active = false; };
  }, [contextRetry, onAuthChange, slug]);

  useEffect(() => {
    if (loading || authed || forceSignedOut || authRedirectStarted.current) { return; }
    authRedirectStarted.current = true;
    const destination = buildPortalAuthUrl();
    if (onAuthRedirect) {
      onAuthRedirect(destination);
    } else {
      navigateToPortalAuth(destination);
    }
  }, [activeView, authed, forceSignedOut, loading, onAuthRedirect, slug]);

  const admin = context?.institutionAdmin;
  const scopes = admin?.scopes || [];
  const has = (scope) => { return scopes.includes(scope); };
  // The Branding page's own rule; Overview gates its branding action on it too.
  const canManageBranding = Boolean(
    context?.superAdmin
    || admin?.role === 'OWNER'
    || has('BRANDING_MANAGE'),
  );

  useEffect(() => {
    let active = true;
    if (!admin || activeView !== 'overview') { return undefined; }
    const initialLoad = !seats && !analytics;
    const requestedWeeks = trendWeeks;
    if (initialLoad) {
      setOverviewLoading(true);
    } else {
      setTrendLoading(true);
    }
    Promise.all([
      (has('SEATS_MANAGE') || has('BILLING_VIEW')) ? settle(getSeats) : null,
      has('ANALYTICS_VIEW')
        ? settle(() => { return getInstitutionAnalytics(undefined, undefined, requestedWeeks); })
        : null,
    ]).then(([seatData, analyticsData]) => {
      if (!active) { return; }
      // A failed read keeps the last good data, so a failed range change does
      // not blank numbers that already loaded.
      setSeatsError(seatData === FAILED);
      if (seatData !== FAILED) { setSeats(seatData); }
      setAnalyticsError(analyticsData === FAILED);
      if (analyticsData !== FAILED) {
        setAnalytics(analyticsData);
        setLoadedTrendWeeks(requestedWeeks);
      }
      // The next skeleton draws the time-saved cell only if this read had it.
      if (analyticsData && analyticsData !== FAILED) {
        writeTimeSavedHint(slug, Boolean(analyticsData.timeSaved));
      }
      setOverviewLoading(false);
      setTrendLoading(false);
    });
    return () => { active = false; };
    // Scope values are derived from the resolved admin record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, activeView, trendWeeks, overviewRetry]);

  useEffect(() => {
    let active = true;
    if (!admin || activeView !== 'overview') { return undefined; }
    // Each signal is read under the scope its own page uses, and fails closed:
    // a failed read drops its row instead of breaking Overview.
    const canReviewAccess = Boolean(context?.superAdmin || admin.role === 'OWNER');
    Promise.all([
      has('SEATS_MANAGE') ? settle(listSeatRequests) : null,
      has('BILLING_VIEW') ? settle(listFundingRequests) : null,
      canReviewAccess ? settle(listPermissionRequests) : null,
    ]).then(([seatRequestData, fundingData, accessData]) => {
      if (!active) { return; }
      const count = (data, pick) => {
        return data && data !== FAILED ? pick(data) : 0;
      };
      setSignals({
        waitingStudents: count(seatRequestData, (data) => { return Number(data.openCount) || 0; }),
        pendingChanges: count(fundingData, (data) => {
          return (data.requests || []).filter((request) => { return request.status === 'PENDING'; }).length;
        }),
        accessRequests: count(accessData, (data) => { return (data.requests || []).length; }),
      });
    });
    return () => { active = false; };
    // Scope values are derived from the resolved admin record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, activeView, overviewRetry]);

  // The shell shows this beside the Overview welcome line, only from loaded
  // seat data.
  const programStatus = seats && (has('SEATS_MANAGE') || has('BILLING_VIEW'))
    ? ((seats.seats?.used || 0) > 0 ? 'Program active' : 'Setup in progress')
    : null;
  useEffect(() => {
    onProgramStatusChange?.(programStatus);
  }, [onProgramStatusChange, programStatus]);

  if (loading) {
    return activeView === 'ai'
      ? <InstitutionAiSkeleton institutionName={branding?.branding?.displayName || branding?.name} />
      : <PortalContentSkeleton slug={slug} />;
  }
  if (forceSignedOut || !authed) {
    return activeView === 'ai'
      ? <InstitutionAiSkeleton institutionName={branding?.branding?.displayName || branding?.name} />
      : <PortalContentSkeleton slug={slug} />;
  }

  if (contextError) {
    return (
      <MessagePanel
        title="Can’t load your admin access"
        body="We couldn’t reach Coffee Chats. Your access has not changed."
        action={(
          <Button
            label="Try again"
            onClick={() => { setContextRetry((value) => { return value + 1; }); }}
          />
        )}
      />
    );
  }

  if (context?.superAdmin && !admin) {
    return (
      <MessagePanel
        title="This portal is for institution administrators"
        body="You are signed in as a platform administrator. Open the admin console to manage schools and platform operations."
        action={<Button label="Open admin console" onClick={() => { window.location.href = 'https://admin.coffeechats.me'; }} />}
      />
    );
  }

  if (!admin) {
    return (
      <MessagePanel
        title="Institution access required"
        body="Your account does not have administrator access for this school. Ask an existing owner to invite you."
      />
    );
  }

  if (activeView === 'analytics') {
    return has('ANALYTICS_VIEW')
      ? <AnalyticsPanel slug={slug} canManageSeats={has('SEATS_MANAGE')} />
      : <ScopeNotice label="analytics" requestedScope="ANALYTICS_VIEW" />;
  }
  if (activeView === 'ai') {
    const institutionBranding = branding?.branding || {};
    return (
      <InstitutionAiView
        institutionName={institutionBranding.displayName || branding?.name || 'your institution'}
        slug={branding?.slug || slug}
        logoFileId={institutionBranding.logoFileId || null}
        logoSource={institutionBranding.logoSource}
        autoLogoDomain={institutionBranding.autoLogoDomain}
        profile={profile}
        conversationId={aiConversationId}
        onConversationChange={onAiConversationChange}
      />
    );
  }
  if (activeView === 'seats') {
    const canViewSeats = has('SEATS_MANAGE') || has('BILLING_VIEW');
    const canRequestBilling = has('BILLING_VIEW');
    // The roster, the weekly job, sits right under the seat summary; the
    // procurement forms fold into one collapsed disclosure below it.
    return canViewSeats ? (
      <div className="flex flex-col gap-4">
        <SeatsPanel
          requestOnly={!context?.superAdmin}
          canEdit={Boolean(context?.superAdmin || canRequestBilling)}
          canResolveRequests={Boolean(context?.superAdmin || has('SEATS_MANAGE'))}
        />
        {has('SEATS_MANAGE') ? <StudentRosterPanel schoolDomain={branding?.branding?.autoLogoDomain || null} /> : null}
        {canRequestBilling ? (
          <Disclosure title="Overage and Enterprise usage">
            <OveragePanel
              requestOnly={!context?.superAdmin}
              canEdit={Boolean(context?.superAdmin || canRequestBilling)}
            />
            <EnterpriseUsagePanel />
          </Disclosure>
        ) : null}
      </div>
    ) : <ScopeNotice label="seat and funding controls" requestedScope="SEATS_MANAGE" />;
  }
  if (activeView === 'billing') {
    return has('BILLING_VIEW')
      ? <InvoicesPanel />
      : <ScopeNotice label="billing and invoices" requestedScope="BILLING_VIEW" />;
  }
  if (activeView === 'admins') {
    return admin.role === 'OWNER' || has('ADMINS_MANAGE') ? (
      <div className="flex flex-col gap-4">
        {admin.role === 'OWNER' || context?.superAdmin ? <PermissionRequestsPanel /> : null}
        <AdminsPanel
          currentEmail={profile?.email || undefined}
          viewer={{ role: admin.role, scopes, superAdmin: Boolean(context?.superAdmin) }}
        />
      </div>
    ) : <ScopeNotice label="team management" requestedScope="ADMINS_MANAGE" />;
  }
  if (activeView === 'audit') {
    return has('ADMINS_MANAGE')
      ? <PortalAuditView />
      : <ScopeNotice label="the audit log" requestedScope="ADMINS_MANAGE" />;
  }
  if (activeView === 'branding') {
    // Team admins may view the identity (it is public branding data); writes
    // stay behind OWNER or BRANDING_MANAGE.
    const canViewBranding = canManageBranding || has('ADMINS_MANAGE');
    return canViewBranding ? (
      <BrandingOverview
        branding={branding}
        slug={slug}
        accent={accent}
        canManage={canManageBranding}
        onBrandingChange={onBrandingChange}
        superAdmin={Boolean(context?.superAdmin)}
      />
    ) : <ScopeNotice label="branding" requestedScope="BRANDING_MANAGE" />;
  }
  if (activeView === 'support') {
    return <PortalSupportView />;
  }

  return (
    <OverviewDashboard
      branding={branding}
      slug={slug}
      seats={seats}
      analytics={analytics}
      seatsError={seatsError}
      analyticsError={analyticsError}
      onRetry={() => { setOverviewRetry((value) => { return value + 1; }); }}
      signals={signals}
      canManageBranding={canManageBranding}
      loading={overviewLoading}
      has={has}
      onNavigate={onNavigate}
      superAdmin={Boolean(context?.superAdmin)}
      trendLoading={trendLoading}
      trendWeeks={trendLoading ? trendWeeks : loadedTrendWeeks}
      onTrendWeeksChange={(weeks) => {
        // Pressing the window that failed to load retries it.
        if (weeks === trendWeeks) {
          setOverviewRetry((value) => { return value + 1; });
        } else {
          setTrendWeeks(weeks);
        }
      }}
    />
  );
}

function OverviewDashboard({
  branding, slug, seats, analytics, seatsError, analyticsError, onRetry, signals,
  canManageBranding, loading, has, onNavigate, superAdmin, trendLoading, trendWeeks, onTrendWeeksChange,
}) {
  const t = useTokens();
  const seatCounts = seats?.seats || {};
  const report = analytics?.report;
  const platformOverview = superAdmin ? analytics?.platformOverview : null;
  const trends = analytics?.trends;
  const analyticsReady = report && !report.suppressed;
  // COF-591: the envelope carries `timeSaved` only while the switch is on and
  // its loader worked; without it the stat, its note and the snapshot row are
  // left out, never shown as zero.
  const timeSaved = analytics?.timeSaved;
  const savedAllTime = timeSaved && !timeSaved.suppressed && !timeSaved.allTime?.suppressed
    ? timeSaved.allTime
    : null;
  const baseOverview = platformOverview || (analyticsReady ? {
    consentedCount: report.adoption?.consentedCount,
    referralConversionRate: report.outcomes?.referralConversionRate,
    coffeeChats: report.activity?.totalCoffeeChats,
  } : null);
  // A withheld all time is null here and reads "Private".
  const aggregateOverview = baseOverview && {
    ...baseOverview,
    timeSavedMinutes: savedAllTime ? savedAllTime.totalMinutes : null,
    timeSavedPerStudentMinutes: savedAllTime ? savedAllTime.perStudentMinutes : null,
  };
  const savedSince = timeSaved?.since
    ? new Date(timeSaved.since).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
    : null;
  const canSeeSeats = has('SEATS_MANAGE') || has('BILLING_VIEW');
  const canSeeAnalytics = has('ANALYTICS_VIEW');
  // Seat rows render only from loaded seat data, never from defaults.
  const seatsLoaded = canSeeSeats && Boolean(seats);
  const seatsUnavailable = canSeeSeats && seatsError && !seats;
  const analyticsUnavailable = canSeeAnalytics && analyticsError && !analytics;
  const seatsConfigured = (seatCounts.purchased || 0) > 0;
  const logoConfigured = Boolean(
    branding?.branding?.logoFileId || branding?.branding?.autoLogoDomain,
  );
  // Why the aggregates are hidden: nobody has joined yet, or the cohort is
  // below the privacy floor. Zero needs seat data this admin can see; no
  // fallback count is assumed. Seat data alone is enough to say zero, so an
  // admin without ANALYTICS_VIEW still sees "None yet" here.
  const zeroStudents = !superAdmin && !aggregateOverview && !analyticsUnavailable
    && seatsLoaded && typeof seats?.seats?.used === 'number' && seats.seats.used === 0;
  const belowFloor = !superAdmin && !aggregateOverview && !analyticsUnavailable && !zeroStudents
    && Boolean(report?.suppressed);
  const minCohort = report?.kAnonymityMin ?? report?.min ?? analytics?.institution?.kAnonymityMin ?? 5;
  const aggregateValue = (format) => {
    if (aggregateOverview) { return format(aggregateOverview); }
    if (analyticsUnavailable) { return 'Unavailable'; }
    if (zeroStudents) { return <span style={{ color: t.muted }}>None yet</span>; }
    return 'Private';
  };
  let seatsUsedValue = `${seatCounts.used || 0} / ${seatCounts.purchased || 0}`;
  if (!canSeeSeats) {
    seatsUsedValue = 'Locked';
  } else if (seatsUnavailable) {
    seatsUsedValue = 'Unavailable';
  }
  let analyticsStatus = 'Privacy threshold pending';
  if (aggregateOverview) {
    analyticsStatus = platformOverview ? 'Platform totals' : 'Reporting';
  } else if (analyticsUnavailable) {
    analyticsStatus = 'Unavailable';
  } else if (!canSeeAnalytics && !superAdmin) {
    analyticsStatus = 'Locked';
  }

  // Priority actions come from real signals, in a fixed order: seats, access,
  // branding. A signal that failed to load contributes no row.
  const plural = (count, one, many) => { return count === 1 ? one : many; };
  const actions = [];
  // Same rule as the Seats page change form, so the action is one this admin can take.
  if ((superAdmin || has('BILLING_VIEW')) && seatsLoaded && !seatsConfigured) {
    actions.push({
      key: 'seats-setup',
      title: 'Configure sponsored seats',
      detail: 'Set the number of students your program will sponsor.',
      status: 'Required',
      view: 'seats',
    });
  }
  const waitingStudents = signals?.waitingStudents || 0;
  if (waitingStudents > 0) {
    actions.push({
      key: 'seat-requests',
      title: 'Students are waiting on a seat',
      detail: `${waitingStudents} ${plural(waitingStudents, 'student has', 'students have')} asked for a sponsored seat.`,
      status: 'Waiting',
      view: 'seats',
    });
  }
  const pendingChanges = signals?.pendingChanges || 0;
  if (pendingChanges > 0) {
    actions.push({
      key: 'funding-change',
      title: 'Seat or funding change in review',
      detail: `${pendingChanges} requested ${plural(pendingChanges, 'change is', 'changes are')} waiting for Coffee Chats to review.`,
      status: 'Pending',
      view: 'seats',
    });
  }
  const accessRequests = signals?.accessRequests || 0;
  if (accessRequests > 0) {
    actions.push({
      key: 'access-requests',
      title: 'Confirm your admin team',
      detail: `${accessRequests} access ${plural(accessRequests, 'request is', 'requests are')} waiting for your review.`,
      status: 'Review',
      view: 'admins',
    });
  }
  if (canManageBranding && !logoConfigured) {
    actions.push({
      key: 'branding',
      title: 'Set up school branding',
      detail: 'A configured identity makes the portal feel owned by your institution.',
      status: 'Needed',
      view: 'branding',
    });
  }

  const trendsVisible = Boolean(trends && !trends.suppressed && trends.weeks?.length);
  const trendsAllZero = trendsVisible && trends.weeks.every((week) => {
    return TREND_KEYS.every((key) => { return !week[key]; });
  });
  const trendRange = trendWeeks === 1 ? 'past week' : `past ${trendWeeks} weeks`;

  if (loading) { return <PortalContentSkeleton slug={slug} />; }

  return (
    <div className="flex flex-col gap-7">
      <section aria-label="Program numbers" className="flex flex-col gap-2">
        <StatRow>
          <Stat label="Seats used" value={seatsUsedValue} />
          <Stat
            label={superAdmin ? 'Student adoption' : 'Students opted in'}
            value={aggregateValue((overview) => {
              return superAdmin && typeof overview.eligibleStudents === 'number'
                ? `${num(overview.consentedCount)} / ${num(overview.eligibleStudents)}`
                : num(overview.consentedCount);
            })}
          />
          <Stat label="Coffee chats" value={aggregateValue((overview) => { return num(overview.coffeeChats); })} />
          <Stat
            label="Referral conversion"
            value={aggregateValue((overview) => { return pct(overview.referralConversionRate); })}
          />
          {timeSaved ? (
            <Stat
              className={FIFTH_STAT_SPAN}
              label="Student hours saved (est.)"
              value={aggregateValue((overview) => {
                return overview.timeSavedMinutes === null ? 'Private' : fmtHours(overview.timeSavedMinutes);
              })}
            />
          ) : null}
        </StatRow>
        {seatsError || analyticsError ? (
          <ErrorState message="Couldn’t load numbers." onRetry={onRetry} />
        ) : null}
        {zeroStudents ? (
          <div className="flex flex-wrap items-center gap-3">
            <Note>No students yet. Invite your first cohort.</Note>
            {has('SEATS_MANAGE') ? (
              <Button small label="Open Seats & funding" onClick={() => { onNavigate('seats'); }} />
            ) : null}
          </div>
        ) : null}
        {belowFloor ? (
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge tone="sand">Below cohort min</StatusBadge>
            <Note>{`Counts appear once ${minCohort}+ students opt in.`}</Note>
          </div>
        ) : null}
        {!zeroStudents && !belowFloor ? (
          <Note>
            {superAdmin
              ? 'Student adoption shows opted-in participants / Coffee Chats accounts with a verified school email domain.'
              : 'Students opted in includes only currently sponsored participants who granted institution access.'}
          </Note>
        ) : null}
        {aggregateOverview ? (
          <Note>Referral conversion is the share of students with at least one completed coffee chat who got a referral.</Note>
        ) : null}
        {timeSaved ? (
          <div className="flex flex-wrap items-center gap-x-3">
            <Note>
              {`Estimated hours opted-in students saved on outreach, chat prep and applications, counted from when each student opted in${savedSince ? ` (no earlier than ${savedSince})` : ''}, for students currently opted in. Students’ time, not staff time.`}
            </Note>
            <button
              type="button"
              onClick={() => { onNavigate('analytics'); }}
              className={`inline-flex items-center min-h-[44px] text-base underline underline-offset-4 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
              style={{ color: t.heading, fontFamily: t.fonts.body }}
            >
              How we estimate
            </button>
          </div>
        ) : null}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8">
        <section>
          <div className="flex items-center justify-between gap-4 mb-3">
            <h2 className="text-xl" style={{ color: t.heading, fontFamily: t.fonts.display }}>
              Priority actions
            </h2>
            {actions.length ? (
              <span className="text-[13px] uppercase tracking-[0.1em]" style={{ color: t.muted, fontFamily: t.fonts.mono }}>
                {`${actions.length} open`}
              </span>
            ) : null}
          </div>
          {actions.length ? (
            <div className="border-y" style={{ borderColor: t.hairline }}>
              {actions.map((action) => {
                return (
                  <ActionRow
                    key={action.key}
                    title={action.title}
                    detail={action.detail}
                    status={action.status}
                    onClick={() => { onNavigate(action.view); }}
                  />
                );
              })}
            </div>
          ) : null}
          {!actions.length && signals ? (
            <p className="border-y py-4 text-base" style={{ borderColor: t.hairline, color: t.muted }}>
              Nothing needs your attention right now.
            </p>
          ) : null}
        </section>

        <section>
          <h2 className="text-xl mb-3" style={{ color: t.heading, fontFamily: t.fonts.display }}>
            Program snapshot
          </h2>
          <div className="border-y py-1" style={{ borderColor: t.hairline }}>
            {seatsLoaded ? <SnapshotRow label="Seat plan" value={seats.seatPlan || 'Not configured'} /> : null}
            <SnapshotRow label="Analytics" value={analyticsStatus} />
            {timeSaved ? (
              <SnapshotRow
                label="Hours saved per opted-in student"
                value={aggregateValue((overview) => {
                  return overview.timeSavedPerStudentMinutes === null
                    ? 'Private'
                    : fmtSavedShare(overview.timeSavedPerStudentMinutes, overview.timeSavedMinutes);
                })}
              />
            ) : null}
            <SnapshotRow label="Portal address" value={`${slug}.coffeechats.me`} mono />
            {seatsLoaded ? (
              <SnapshotRow label="Overage funding" value={seats.overagePool?.enabled ? 'Enabled' : 'Off'} />
            ) : null}
          </div>
        </section>
      </div>

      {trendsVisible ? (
        <section>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-3">
            <div>
              <h2 className="text-xl" style={{ color: t.heading, fontFamily: t.fonts.display }}>
                Network growth
              </h2>
              <p className="mt-1 text-sm" style={{ color: t.muted }}>
                {trendWeeks === 52
                  ? 'One year'
                  : trendWeeks === 1 ? 'Past week' : `${trendWeeks} weeks`} of aggregate participation and completed coffee chats.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-[13px] uppercase tracking-[0.1em]" style={{ color: t.muted, fontFamily: t.fonts.mono }}>
                {trendLoading ? 'Updating' : (trends.exact ? 'Platform aggregate' : 'Privacy-safe buckets')}
              </span>
              {[1, 4, 12, 26, 52].map((weeks) => {
                const active = trendWeeks === weeks;
                return (
                  <button
                    key={weeks}
                    type="button"
                    aria-pressed={active}
                    aria-label={weeks === 1 ? 'Past week' : `${weeks} weeks`}
                    onClick={() => { onTrendWeeksChange(weeks); }}
                    className={`inline-flex items-center min-h-[44px] rounded-full border px-3 text-[13px] font-semibold transition-opacity hover:opacity-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
                    style={{
                      borderColor: active ? t.heading : t.controlBorder,
                      backgroundColor: active ? t.heading : t.surface,
                      color: active ? t.surface : t.body,
                    }}
                  >
                    {weeks}w
                  </button>
                );
              })}
            </div>
          </div>
          {trendsAllZero ? (
            <p className="border-y py-4 text-base" style={{ borderColor: t.hairline, color: t.muted }}>
              {`No coffee-chat activity in the ${trendRange} yet.`}
            </p>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 transition-opacity" style={{ opacity: trendLoading ? 0.55 : 1 }}>
              <div className="rounded-2xl border p-4" style={{ borderColor: t.border, backgroundColor: t.statBg }}>
                <Suspense fallback={<div className="h-[220px] animate-pulse rounded-xl" style={{ backgroundColor: t.hairline }} />}>
                  <TrendChart
                    title="Students participating"
                    data={trends.weeks}
                    xKey="week"
                    height={220}
                    series={[
                      { key: 'students', label: 'Students enrolled', color: '#4A2C1D' },
                      { key: 'studentsNetworking', label: 'Students networking', color: '#6F4E37' },
                    ]}
                  />
                </Suspense>
              </div>
              <div className="rounded-2xl border p-4" style={{ borderColor: t.border, backgroundColor: t.statBg }}>
                <Suspense fallback={<div className="h-[220px] animate-pulse rounded-xl" style={{ backgroundColor: t.hairline }} />}>
                  <TrendChart
                    title="Connections made"
                    data={trends.weeks}
                    xKey="week"
                    variant="bar"
                    height={220}
                    series={[
                      { key: 'coffeeChats', label: 'Coffee chats', color: '#4A2C1D' },
                      { key: 'alumniReached', label: 'Alumni reached', color: '#9A745C' },
                    ]}
                  />
                </Suspense>
              </div>
              <div className="rounded-2xl border p-4" style={{ borderColor: t.border, backgroundColor: t.statBg }}>
                <Suspense fallback={<div className="h-[220px] animate-pulse rounded-xl" style={{ backgroundColor: t.hairline }} />}>
                  <TrendChart
                    title="Outreach activity"
                    data={trends.weeks}
                    xKey="week"
                    height={220}
                    series={[
                      { key: 'emailsSent', label: 'Emails', color: '#4A2C1D' },
                      { key: 'followUps', label: 'Follow-ups', color: '#6F4E37' },
                      { key: 'emailOpens', label: 'Opens', color: '#5A7A8C' },
                    ]}
                  />
                </Suspense>
              </div>
              <div className="rounded-2xl border p-4" style={{ borderColor: t.border, backgroundColor: t.statBg }}>
                <Suspense fallback={<div className="h-[220px] animate-pulse rounded-xl" style={{ backgroundColor: t.hairline }} />}>
                  <TrendChart
                    title="Coffee-chat minutes"
                    data={trends.weeks}
                    xKey="week"
                    variant="bar"
                    height={220}
                    series={[
                      { key: 'coffeeChatMinutes', label: 'Meeting minutes', color: '#9A745C' },
                    ]}
                  />
                </Suspense>
              </div>
            </div>
          )}
          <Note className="mt-3">
            Aggregate counts only. Student and alumni identities are never included in this view.
          </Note>
        </section>
      ) : null}
    </div>
  );
}

function ActionRow({ title, detail, status, onClick }) {
  const t = useTokens();
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full min-h-[76px] py-3 flex items-center justify-between gap-4 text-left border-b last:border-b-0 hover:opacity-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
      style={{ borderColor: t.hairline }}
    >
      <span className="min-w-0">
        <span className="block text-[16px] font-semibold" style={{ color: t.heading }}>{title}</span>
        <span className="block mt-0.5 text-sm" style={{ color: t.muted }}>{detail}</span>
      </span>
      {status ? <StatusBadge>{status}</StatusBadge> : null}
    </button>
  );
}

function SnapshotRow({ label, value, mono = false }) {
  const t = useTokens();
  return (
    <div className="min-h-[52px] flex items-center justify-between gap-3 border-b last:border-b-0" style={{ borderColor: t.hairline }}>
      <span className="text-sm" style={{ color: t.muted }}>{label}</span>
      <span className="text-sm text-right" style={{ color: t.heading, fontFamily: mono ? t.fonts.mono : t.fonts.body }}>
        {value}
      </span>
    </div>
  );
}

function BrandingOverview({
  branding, slug, accent, canManage, onBrandingChange, superAdmin = false,
}) {
  const t = useTokens();
  const [uploading, setUploading] = useState(false);
  const [sourceBusy, setSourceBusy] = useState(false);
  const [slugBusy, setSlugBusy] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const schoolName = branding?.branding?.displayName || branding?.name || 'Your school';
  const storedDisplayName = branding?.branding?.displayName || branding?.name || '';
  const storedAccent = branding?.branding?.accentColor || '';
  const storedSlug = branding?.slug || slug || '';
  const [displayNameDraft, setDisplayNameDraft] = useState(storedDisplayName);
  const [accentDraft, setAccentDraft] = useState(storedAccent);
  const [slugDraft, setSlugDraft] = useState(storedSlug);
  const logoFileId = branding?.branding?.logoFileId || null;
  const logoSource = branding?.branding?.logoSource;
  const autoLogoDomain = branding?.branding?.autoLogoDomain || null;
  // An unset or half-typed accent shows a neutral swatch, not a stand-in color.
  const accentValid = /^#[0-9a-f]{6}$/i.test(accentDraft);

  useEffect(() => {
    setDisplayNameDraft(storedDisplayName);
    setAccentDraft(storedAccent);
  }, [storedAccent, storedDisplayName]);

  useEffect(() => {
    setSlugDraft(storedSlug);
  }, [storedSlug]);

  const savePortalAddress = async () => {
    const nextSlug = slugDraft.trim().toLowerCase();
    if (!nextSlug || nextSlug === storedSlug) { return; }
    setSlugBusy(true);
    try {
      const updated = await renameInstitutionPortalSlug(nextSlug);
      setSlugDraft(updated.slug);
      onBrandingChange?.({ ...branding, slug: updated.slug, previousSlugs: updated.previousSlugs });
      notifySuccess('Portal address updated. Existing links will continue to work.');
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Could not update the portal address.');
    } finally {
      setSlugBusy(false);
    }
  };

  const saveBrandProfile = async () => {
    const displayName = displayNameDraft.trim();
    const accentColor = accentDraft.trim();
    if (!displayName) {
      setProfileMessage('Display name is required.');
      return;
    }
    const patch = {};
    if (displayName !== storedDisplayName) { patch.displayName = displayName; }
    if (accentColor !== storedAccent) {
      if (!accentColor) {
        setProfileMessage('Enter a 6-digit accent color such as #00693E.');
        return;
      }
      patch.accentColor = accentColor;
    }
    if (Object.keys(patch).length === 0) {
      notifySuccess('Brand profile is already up to date.');
      return;
    }
    setProfileBusy(true);
    setProfileMessage('');
    try {
      const updated = await updateInstitutionBranding(patch);
      onBrandingChange?.(updated);
      notifySuccess('Brand profile saved.');
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Could not save the brand profile.');
    } finally {
      setProfileBusy(false);
    }
  };

  const selectLogoSource = async (source) => {
    setUploadError('');
    setSourceBusy(true);
    try {
      const updated = await setInstitutionLogoSource(source);
      onBrandingChange?.(updated);
      notifySuccess(source === 'AUTO' ? 'Automatic school logo is active.' : 'Uploaded school logo is active.');
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Could not switch the active logo.');
    } finally {
      setSourceBusy(false);
    }
  };

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) { return; }
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setUploadError('Choose a PNG, JPEG, or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Choose an image that is 5 MB or smaller.');
      return;
    }
    setUploadError('');
    setUploading(true);
    try {
      const updated = await uploadInstitutionLogo(file);
      if (onBrandingChange) { onBrandingChange(updated); }
      notifySuccess('School logo uploaded.');
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Could not upload the logo. Try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-8">
      <section>
        <h2 className="text-xl mb-3" style={{ color: t.heading, fontFamily: t.fonts.display }}>
          Portal identity
        </h2>
        <div className="border-y py-6" style={{ borderColor: t.hairline }}>
          <InstitutionMark
            name={schoolName}
            slug={slug}
            logoFileId={logoFileId}
            logoSource={logoSource}
            autoLogoDomain={autoLogoDomain}
            size={80}
            showName
          />
          {canManage ? (
            <div className="mt-6">
              {logoFileId ? (
                <>
                  <label
                    className={`inline-flex min-h-[44px] items-center justify-center rounded-full border px-5 text-sm font-medium cursor-pointer focus-within:ring-2 focus-within:ring-offset-2 ${t.focusRing}`}
                    style={{
                      color: t.secondaryText,
                      backgroundColor: t.secondaryBg,
                      borderColor: t.secondaryBorder,
                      opacity: uploading ? 0.55 : 1,
                    }}
                  >
                    {uploading ? 'Uploading...' : 'Replace uploaded logo'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="sr-only"
                      disabled={uploading}
                      onChange={uploadLogo}
                    />
                  </label>
                  <p className="mt-2 text-sm" style={{ color: t.muted }}>
                    PNG, JPEG, or WebP. Maximum 5 MB.
                  </p>
                </>
              ) : null}
              <div className={logoFileId ? 'mt-5' : ''}>
                <InstitutionLogoPicker
                  name={schoolName}
                  slug={slug}
                  logoFileId={logoFileId}
                  logoSource={logoSource}
                  autoLogoDomain={autoLogoDomain}
                  busy={sourceBusy || uploading}
                  onSelect={selectLogoSource}
                  onUpload={uploadLogo}
                  uploading={uploading}
                />
              </div>
              {uploadError ? (
                <p className="mt-2 text-sm" style={{ color: t.danger }}>{uploadError}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
      <section>
        <h2 className="text-xl mb-3" style={{ color: t.heading, fontFamily: t.fonts.display }}>
          Brand profile
        </h2>
        {canManage ? (
          <div className="border-y py-5" style={{ borderColor: t.hairline }}>
            <label htmlFor="institution-display-name" className="block text-sm font-semibold" style={{ color: t.heading }}>
              Display name
            </label>
            <input
              id="institution-display-name"
              value={displayNameDraft}
              onChange={(event) => { setDisplayNameDraft(event.target.value); setProfileMessage(''); }}
              placeholder="Dartmouth College"
              maxLength={80}
              className={`mt-2 w-full min-h-[44px] rounded-md border px-3 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
              style={{ backgroundColor: t.inputBg, color: t.inputText, borderColor: t.inputBorder }}
            />

            <label htmlFor="institution-accent-color" className="mt-5 block text-sm font-semibold" style={{ color: t.heading }}>
              Accent color
            </label>
            <div className="mt-2 flex items-center gap-3">
              <input
                type="color"
                aria-label="Choose accent color"
                value={accentValid ? accentDraft : t.border}
                onChange={(event) => { setAccentDraft(event.target.value.toUpperCase()); setProfileMessage(''); }}
                className="h-11 w-14 cursor-pointer rounded-md border p-1"
                style={{ backgroundColor: t.inputBg, borderColor: t.inputBorder }}
              />
              <input
                id="institution-accent-color"
                value={accentDraft}
                onChange={(event) => { setAccentDraft(event.target.value); setProfileMessage(''); }}
                placeholder="e.g. #00693E"
                maxLength={7}
                className={`min-h-[44px] flex-1 rounded-md border px-3 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
                style={{ backgroundColor: t.inputBg, color: t.inputText, borderColor: t.inputBorder, fontFamily: t.fonts.mono }}
              />
            </div>
            {accentDraft.trim() ? null : (
              <p className="mt-2 text-sm" style={{ color: t.muted }}>
                Not set. The header rule uses the default neutral color.
              </p>
            )}
            <p className="mt-2 text-sm" style={{ color: t.muted }}>
              Use a dark 6-digit hex color that remains readable on the portal background.
            </p>

            <div className="mt-5 border-t pt-5" style={{ borderColor: t.hairline }}>
              <label htmlFor="institution-portal-slug" className="block text-sm font-semibold" style={{ color: t.heading }}>
                Portal address
              </label>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex min-h-[44px] flex-1 items-center rounded-md border px-3" style={{ backgroundColor: t.inputBg, borderColor: t.inputBorder }}>
                  <input
                    id="institution-portal-slug"
                    value={slugDraft}
                    onChange={(event) => { setSlugDraft(event.target.value.toLowerCase()); }}
                    maxLength={63}
                    autoCapitalize="none"
                    autoComplete="off"
                    spellCheck="false"
                    className="min-w-0 flex-1 border-0 bg-transparent text-base focus:outline-none"
                    style={{ color: t.inputText, fontFamily: t.fonts.mono }}
                  />
                  <span className="shrink-0 text-sm" style={{ color: t.muted, fontFamily: t.fonts.mono }}>.coffeechats.me</span>
                </div>
                <Button
                  small
                  variant="secondary"
                  label={slugBusy ? 'Updating…' : 'Update address'}
                  onClick={savePortalAddress}
                  disabled={slugBusy || !slugDraft.trim() || slugDraft.trim().toLowerCase() === storedSlug}
                />
              </div>
              {superAdmin ? (
                <p className="mt-2 text-sm" style={{ color: t.muted }}>
                  Previous portal links remain valid after a change.
                </p>
              ) : (
                <div className="mt-2">
                  <p className="text-sm" style={{ color: t.muted }}>
                    Address changes are set up by Coffee Chats support. Previous portal links remain valid after a change.
                  </p>
                  <a
                    href={`${VIEW_TO_PATH.support}?type=GENERAL`}
                    className={`mt-1 min-h-[44px] inline-flex items-center text-sm underline focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
                    style={{ color: t.heading }}
                  >
                    Contact support
                  </a>
                </div>
              )}
              <div className="mt-4 border-t" style={{ borderColor: t.hairline }}>
              <SnapshotRow
                label="Active school logo"
                value={logoSource === 'UPLOADED' || (!logoSource && logoFileId)
                  ? 'Uploaded override'
                  : autoLogoDomain ? `Automatic · ${autoLogoDomain}` : 'Not configured'}
              />
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <Button
                label={profileBusy ? 'Saving…' : 'Save brand profile'}
                onClick={saveBrandProfile}
                disabled={profileBusy}
              />
              {profileMessage ? (
                <span className="text-sm" role="status" style={{ color: t.muted }}>{profileMessage}</span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="border-y py-1" style={{ borderColor: t.hairline }}>
            <SnapshotRow label="Display name" value={schoolName} />
            <SnapshotRow label="Portal address" value={`${slug}.coffeechats.me`} mono />
            <SnapshotRow
              label="Active school logo"
              value={logoSource === 'UPLOADED' || (!logoSource && logoFileId)
                ? 'Uploaded override'
                : autoLogoDomain ? `Automatic · ${autoLogoDomain}` : 'Not configured'}
            />
            <SnapshotRow label="Accent color" value={accent || 'Coffee Chats default'} mono />
          </div>
        )}
        <p className="mt-4 text-sm leading-relaxed" style={{ color: t.muted }}>
          {canManage
            ? 'Changes update the portal header and sign-in screen for your institution.'
            : 'You can review the school identity here. Ask an owner for the Branding permission to make changes.'}
        </p>
      </section>
    </div>
  );
}

function ScopeNotice({ label, requestedScope }) {
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    listMyPermissionRequests().then((data) => {
      if (!active) { return; }
      setPending((data.requests || []).some((request) => {
        return request.requestedScope === requestedScope && request.status === 'PENDING';
      }));
    }).catch(() => {});
    return () => { active = false; };
  }, [requestedScope]);

  const requestAccess = async () => {
    setBusy(true);
    try {
      await requestInstitutionPermission({
        requestedScope,
        reason: `Access requested from the ${label} page.`,
      });
      setPending(true);
      notifySuccess('Permission request sent to the institution owners.');
    } catch (error) {
      if (error?.response?.status === 409) {
        setPending(true);
      } else {
        notifyError(error?.response?.data?.error || 'Could not request permission.');
      }
    } finally {
      setBusy(false);
    }
  };

  // The same open hairline block as MessagePanel: no ghost preview, scrim or
  // shadow (DESIGN.md Slop Blacklist bans glass).
  return (
    <MessagePanel
      title="Permission required"
      body={`Your role does not include access to ${label}. An institution owner can grant it.`}
      action={(
        <Button
          label={pending ? 'Request pending' : busy ? 'Sending…' : 'Request access'}
          onClick={requestAccess}
          disabled={pending || busy}
        />
      )}
    />
  );
}

/**
 * A collapsed-by-default section with a 44px heading button. Children mount
 * only when open, so their reads wait until someone asks for them.
 */
function Disclosure({ title, children }) {
  const t = useTokens();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <section className="border-t" style={{ borderColor: t.hairline }}>
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => { setOpen((value) => { return !value; }); }}
          className={`w-full min-h-[44px] py-3 flex items-center justify-between gap-3 text-left text-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
          style={{ color: t.heading, fontFamily: t.fonts.display }}
        >
          <span className="font-medium">{title}</span>
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className={`h-5 w-5 shrink-0 ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </h2>
      {open ? <div id={panelId} className="flex flex-col gap-4 pt-2">{children}</div> : null}
    </section>
  );
}

function MessagePanel({ title, body, action }) {
  const t = useTokens();
  return (
    <section className="max-w-2xl py-8 border-y" style={{ borderColor: t.hairline }}>
      <h2 className="text-xl" style={{ color: t.heading, fontFamily: t.fonts.display }}>{title}</h2>
      <p className="mt-2 text-base leading-relaxed" style={{ color: t.muted }}>{body}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </section>
  );
}

function PortalContentSkeleton({ slug }) {
  const t = useTokens();
  // Time saved is the fifth stat only while its switch is on, so the fifth
  // cell follows this school's last analytics read (no read yet: four).
  const cells = readTimeSavedHint(slug) ? 5 : 4;
  return (
    <div className="animate-pulse" aria-label="Loading portal">
      <div className="h-7 w-64 rounded" style={{ backgroundColor: t.hairline }} />
      <div className="mt-3 h-4 w-80 max-w-full rounded" style={{ backgroundColor: t.hairline }} />
      {/* The Overview StatRow's grid and cell boxes, the fifth spanning where
          five sit 2 across. */}
      <div className="mt-7 rounded-xl border overflow-hidden" style={{ borderColor: t.border, backgroundColor: t.statBg }}>
        <div className={`grid gap-px ${STAT_GRID_COLS[cells]}`}>
          {Array.from({ length: cells }, (_, item) => {
            return (
              <div
                key={item}
                className={`min-h-[5.75rem] sm:min-h-[5.25rem] ${item === 4 ? FIFTH_STAT_SPAN : ''}`}
                style={{ backgroundColor: t.statBg, boxShadow: `0 0 0 1px ${t.hairline}` }}
              />
            );
          })}
        </div>
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="h-56 rounded" style={{ backgroundColor: t.statBg }} />
        <div className="h-56 rounded" style={{ backgroundColor: t.statBg }} />
      </div>
    </div>
  );
}
